import { virtualizedScrollbarProps } from '../../props'

import {
  BAR_MAP,
  cAF,
  rAF,
  HORIZONTAL,
  SCROLLBAR_MIN_SIZE,
  ScrollbarDirKey,
  renderThumbStyle
} from '../../utils'
import { SCROLLBAR_POSITION, UPDATE_STORE_PARAMS, VERTICAL_SCROLL_BAR_LAYOUT, HORIZONTAL_SCROLL_BAR_LAYOUT } from '../../config'
import '../../style/scroll-bar.scss'

const scrollBarOutside = -12
const scrollBarBottom = 2

function getTrackBottom(scrollbarPosition, layout, offset = 0) {
  let bottomVal = scrollBarBottom
  if (layout === HORIZONTAL) {
    // 外部展示
    if (scrollbarPosition === SCROLLBAR_POSITION[0]) {
      bottomVal = scrollBarOutside + -offset
    } else {
      bottomVal = bottomVal + -offset
    }
  }
  return `${bottomVal}px`
}

export function createScrollBar() {
  let frameHandle = null
  let onselectstartStore = null
  return {
    name: 'KyeScrollBar',
    props: virtualizedScrollbarProps,
    data() {
      return {
        state: {
          isDragging: false,
          traveled: this.scrollFrom,
        },
      }
    },
    watch: {
      thumbStyle: {
        handler: function (styles) {
          this.updateScrollBarStatus(styles)
        },
        immediate: true
      }
    },
    computed: {
      GAP () {
        return this.startGap + this.endGap
      },
      bar () {
        return BAR_MAP[this.layout]
      },
      trackSize () {
        return this.clientSize - this.GAP
      },
      trackStyle() {
        if (!Number.isFinite(this.thumbSize)) {
          return {
            display: 'none',
          }
        }
        const layout = this.layout
        const COMMON_STYLE = {
          width: `${
            HORIZONTAL === layout ? this.trackSize : this.scrollbarSize
          }px`,
          height: `${
            HORIZONTAL === layout ? this.scrollbarSize : this.trackSize
          }px`,
          borderRadius: '4px',
        }
        const scrollbarStyle = this.$props.scrollbarStyle
        if (scrollbarStyle && scrollbarStyle[layout]) {
          return {
            ...scrollbarStyle[layout],
            ...COMMON_STYLE
          }
        }
        return {
          ...COMMON_STYLE,
          position: 'absolute',
          right: '0px',
          bottom: getTrackBottom(this.scrollbarPosition, layout, this.offset),
          [ScrollbarDirKey[layout]]: '0',
        }
      },
      thumbSize () {
        const ratio = this.ratio
        const clientSize = this.clientSize
        if (ratio >= 100) {
          return Number.POSITIVE_INFINITY
        }
        if (ratio >= 50) {
          return (ratio * clientSize) / 100 // clientSize等于视口的宽度
        }
        const SCROLLBAR_MAX_SIZE = clientSize / 3
        return Math.floor(
          Math.min(
            Math.max(ratio * clientSize, SCROLLBAR_MIN_SIZE),
            SCROLLBAR_MAX_SIZE
          )
        )
      },
      thumbStyle () {
        const thumb = `${this.thumbSize}px`
        const style = renderThumbStyle(
          {
            bar: this.bar,
            size: thumb,
            move: this.state.traveled,
          },
          this.layout
        )
        return style
      },
      totalSteps () {
        return Math.floor(this.clientSize - this.thumbSize - this.GAP)
      },
      alwaysVisibleSrcollbar () {
        return this.alwaysOn || this.state.isDragging
      },
      scrollInfo() {
        return [this.scroll.scrollLeft, this.scroll.scrollTop]
      }
    },
    beforeDestory() {
      this.detachEvents()
    },
    render(h) {
      return h(
        'div',
        {
          role: 'presentation',
          ref: 'trackRef',
          class: [
            'virtual-srcollbar',
            this.className,
            (this.alwaysVisibleSrcollbar) && 'always-on',
          ],
          style: this.trackStyle,
          on: {
            mousedown: this.clickTrackHandler,
            touchstartPrevent: this.onThumbMouseDown,
          }
        },
        [h(
          'div',
          {
            ref: 'thumbRef',
            class: ['virtual-srcollbar-thumb', this.state.isDragging ? 'virtual-srcollbar-thumb-hover' : ''],
            style: this.thumbStyle,
            on: {
              mousedown: this.onThumbMouseDown,
            }
          },
          []
        )]
      )
    },
    methods: {
      clickTrackHandler (e) {
        const thumbRef = this.$refs.thumbRef
        const offset = Math.abs((e.target).getBoundingClientRect()[this.bar.direction] - e[this.bar.client])
        const thumbHalf = thumbRef[this.bar.offset] / 2
        const distance = offset - thumbHalf
        // 点击时候的步长
        this.state.traveled = Math.max(0, Math.min(distance, this.totalSteps))
        this.$emit('scroll', distance, this.totalSteps)
      },
      onThumbMouseDown (e) {
        e.stopImmediatePropagation()
        if (
          e.ctrlKey ||
          [1, 2].includes(e.button)
        ) {
          return
        }
        // 按下之后的逻辑
        this.state.isDragging = true
        // 滑块容器的size - （点击的位置距离视口的x/y - 滑块容器距离视口的top/left值）
        this.state[this.bar.axis] = e.currentTarget[this.bar.offset] - (e[this.bar.client] - e.currentTarget.getBoundingClientRect()[this.bar.direction])
        // 开始移动
        this.$emit('start-move')
        this.attachEvents()
      },
      // 实现了拖动功能，可以通过鼠标或触摸设备来拖动指定的元素。
      // 当用户按住鼠标左键或在触摸设备上滑动手指时,捕捉到鼠标或手指的位置变化，并相应地更新元素的位置或执行其他相关操作
      attachEvents () {
        // 监听鼠标在整个窗口上的移动和鼠标按钮松开的事件。 模拟常规的滚动条操作，比如我在按下滑块之后，把鼠标移出滑块之后，滚动条还是能滚动、且页面是能正常滚动的
        // 如果不使用这段代码。则会造成。滚动只能发生在滑块内。移出滑块则不能滚动了。
        window.addEventListener('mousemove', this.onMouseMove)
        window.addEventListener('mouseup', this.onMouseUp)
        // 表示拖动的元素的引用
        const thumbEl = this.$refs.thumbRef
        if (!thumbEl) return
        // 阻止文本选择操作（例如鼠标拖动时选中文本）。 用户尝试选择文本时，浏览器会触发selectstart事件
        // 在拖动元素时防止意外的文本选中
        onselectstartStore = document.onselectstart
        document.onselectstart = () => false
        // 处理触摸设备上的拖动操作 触摸设备上用户的手指在屏幕上滑动时触发
        // 按下滑块。滚动内容
        thumbEl.addEventListener('touchmove', this.onMouseMove)
        // 在触摸设备上的手指离开屏幕时触发的事件。
        thumbEl.addEventListener('touchend', this.onMouseUp)
      },
      onMouseUp() {
        this.state.isDragging = false
        this.state[this.bar.axis] = 0
        this.$emit('stop-move')
        this.detachEvents()
      },
      onMouseMove (e) {
        const { isDragging } = this.state
        // 轨道元素
        const trackRef = this.$refs.trackRef
        // 拖动元素
        const thumbRef = this.$refs.thumbRef
        if (!isDragging) return
        if (!thumbRef || !trackRef) return
        // 在拖动开始时记录的值 滑块的size - 鼠标按下的位置的坐标值
        const prevPage = this.state[this.bar.axis]
        if (!prevPage) return
        cAF(frameHandle)
        // 计算当前轨道元素的偏移位置（相对于视口的位置）和当前指针的位置，来获取指针相对于轨道的相对位置
        const offset = (trackRef.getBoundingClientRect()[this.bar.direction] - e[this.bar.client]) * -1
        // 计算出拖动元素在点击位置的偏移量，即thumbClickPosition，通过拖动元素的偏移属性（offsetTop或offsetLeft）减去之前的页面位置（拖动开始时记录的值）
        const thumbClickPosition = thumbRef[this.bar.offset] - prevPage
        /**
         *  +--------------+                                   +--------------+
         *  |              -  <--------- thumb.offsetTop       |              |
         *  |             |+|             <--+                 |              |
         *  |              -                 |                 |              |
         *  |   Content    |                 |                 |              |
         *  |              |                 |                 |              |
         *  |              |                 |                 |              |
         *  |              |                 |                 |              -
         *  |              |                 +-->              |             |+|
         *  |              |                                   |              -
         *  +--------------+                                   +--------------+
         */
        // 计算出拖动的距离，即distance，通过当前位置的偏移量减去拖动元素在点击位置的偏移量。
        const distance = offset - thumbClickPosition
        frameHandle = rAF(() => {
          this.$emit('scroll', distance <= 0 ? 0 : distance, this.totalSteps)
        })
      },
      updateTraveled(scrollFrom) {
        this.state.traveled = Math.ceil(scrollFrom * this.totalSteps)
      },
      // 移除监听
      detachEvents () {
        const thumbRef = this.$refs.thumbRef
        window.removeEventListener('mousemove', this.onMouseMove)
        window.removeEventListener('mouseup', this.onMouseUp)
        document.onselectstart = onselectstartStore
        onselectstartStore = null
        const thumbEl = thumbRef
        if (!thumbEl) return
        thumbEl.removeEventListener('touchmove', this.onMouseMove)
        thumbEl.removeEventListener('touchend', this.onMouseUp)
      },
      updateScrollBarStatus(styles) {
        const layoutMaps = {
          [HORIZONTAL_SCROLL_BAR_LAYOUT]: 'hScrollbarShow',
          [VERTICAL_SCROLL_BAR_LAYOUT]: 'vScrollbarShow'
        }
        const storeKeyName = layoutMaps[this.layout]
        if (!this.$parent.store || !storeKeyName) return
        this.$parent.store.commit(UPDATE_STORE_PARAMS, {
          [storeKeyName]: styles.display !== 'none' // 滚动条已需要显示
        })
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/components/scroll-bar/index.js